import { dsClass } from '../../../common/constants/dsClasses.js';
import { LaitenettiOfferCode } from '../../../common/constants/subscriptionConstants';
import {
  ElisaYrityspuheUusiOfferCode,
  FiveGMobileBroadbandOfferCode,
  FiveGMobileBroadbandProductCode,
  FiveGVoiceOfferCode,
  FiveGVoiceProductCode,
  MobileBroadbandFourGOfferCodes,
  MobileBroadbandFourGProductCodes,
  MobileM2MAddonCode,
  ModelType,
  OldVoiceOfferCode,
  OldVoiceProductCode,
  VoiceYrityspuheLiteProductCode,
  YrityspakettiOfferCode,
  YrityspakettiProductCode,
} from '../../../common/enums.js';
import { elsewhereInEuMsg, t, unlimitedDataInFinlandNordicBalticMsg } from '../../../common/i18n/index.js';
import { productVariables } from './variables';
import type { CampaignAssociation, Offer } from '../../../generated/api/models.js';
import type { ProductPriceTexts } from '../CampaignProductCard.js';
import type { JSX } from 'react';

export interface ProductContent {
  title?: string;
  productNameAddon?: string;
  headerContent?: JSX.Element;
  imageUrl?: string;
  offerCodes: string[];
  isDefaultSelected?: boolean;
  offerName?: string;
}

export interface LegacyProductContent {
  dataLimitation: string;
  offerCode: string;
  modelType: ModelType;
  title?: string;
  priceTexts?: ProductPriceTexts;
  openingFee?: number;
  campaignAssociation?: CampaignAssociation;
  serviceFee?: number;
}

export interface ProductContentWithAddonCode extends ProductContent {
  addOnCode: string;
}

export const productContentMatchesOffer = (
  content: ProductContent | ProductContentWithAddonCode,
  offer: Offer
): boolean => {
  if ('addOnCode' in content) {
    const addonCodes = offer.commercialProducts.flatMap(cp => cp.associatedAddOns?.map(addon => addon.addOnCode));
    return content.offerCodes.includes(offer.offerCode) && addonCodes.includes(content.addOnCode);
  } else {
    return content.offerCodes.includes(offer.offerCode);
  }
};

// These are ordered in the page in the order they appear here.
export const mobileBroadbandFiveGContent = (): ProductContent[] => [
  { offerCodes: [FiveGMobileBroadbandOfferCode.MBB_1000_M], title: '1000M' },
  { offerCodes: [FiveGMobileBroadbandOfferCode.MBB_600_M], title: '600M' },
  { offerCodes: [FiveGMobileBroadbandOfferCode.MBB_300_M], title: '300M' },
  { offerCodes: [FiveGMobileBroadbandOfferCode.MBB_100_M], title: '100M' },
];

// These are ordered in the page in the order they appear here.
// TODO didn't find any use case for the text fields, can possibly be removed
export const m2mContent = (): ProductContentWithAddonCode[] => [
  { offerCodes: [LaitenettiOfferCode.MOBILE_M2M_OFFER_CODE], addOnCode: MobileM2MAddonCode.S, title: 'S' },
  { offerCodes: [LaitenettiOfferCode.MOBILE_M2M_OFFER_CODE], addOnCode: MobileM2MAddonCode.M, title: 'M' },
  { offerCodes: [LaitenettiOfferCode.MOBILE_M2M_OFFER_CODE], addOnCode: MobileM2MAddonCode.L, title: 'L' },
  { offerCodes: [LaitenettiOfferCode.MOBILE_M2M_OFFER_CODE_5G], addOnCode: MobileM2MAddonCode.fiveG },
];

// These are ordered in the page in the order they appear here.
// body, footerText, specsText are not used
export const mobileVoiceContent = (): ProductContent[] => [
  { offerCodes: [OldVoiceOfferCode.VOICE_1000_M], title: '1000M' },
  { offerCodes: [OldVoiceOfferCode.VOICE_600_M], title: '600M' },
  { offerCodes: [OldVoiceOfferCode.VOICE_300_M], title: '300M' },
  { offerCodes: [OldVoiceOfferCode.VOICE_100_M], title: '100M' },
  { offerCodes: [OldVoiceOfferCode.VOICE_10_M], title: '10M' },
];

const populateDataLimitationMessage = (productCode: string): string =>
  `${t.SDTI(unlimitedDataInFinlandNordicBalticMsg)}. ${t.W8I6(elsewhereInEuMsg)}
            ${productVariables[productCode].euFupLimit?.()}.`;

export const getLegacyOffersContentBase = (): LegacyProductContent[] => [
  {
    dataLimitation: populateDataLimitationMessage(MobileBroadbandFourGProductCodes.ONE_FIFTY),
    offerCode: MobileBroadbandFourGOfferCodes.ONE_FIFTY,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(MobileBroadbandFourGProductCodes.FIFTY),
    offerCode: MobileBroadbandFourGOfferCodes.FIFTY,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(MobileBroadbandFourGProductCodes.TEN),
    offerCode: MobileBroadbandFourGOfferCodes.TEN,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(OldVoiceProductCode.VOICE_1000_M),
    offerCode: OldVoiceOfferCode.VOICE_1000_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(OldVoiceProductCode.VOICE_600_M),
    offerCode: OldVoiceOfferCode.VOICE_600_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(OldVoiceProductCode.VOICE_300_M),
    offerCode: OldVoiceOfferCode.VOICE_300_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(OldVoiceProductCode.VOICE_100_M),
    offerCode: OldVoiceOfferCode.VOICE_100_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(OldVoiceProductCode.VOICE_10_M),
    offerCode: OldVoiceOfferCode.VOICE_10_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_1000_M),
    offerCode: YrityspakettiOfferCode.VOICE_1000_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_600_M),
    offerCode: YrityspakettiOfferCode.VOICE_600_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_300_M),
    offerCode: YrityspakettiOfferCode.VOICE_300_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_200_M),
    offerCode: YrityspakettiOfferCode.VOICE_200_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_100_M),
    offerCode: YrityspakettiOfferCode.VOICE_100_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_50_M),
    offerCode: YrityspakettiOfferCode.VOICE_50_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_4G_50_M),
    offerCode: YrityspakettiOfferCode.VOICE_4G_50_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_10_M),
    offerCode: YrityspakettiOfferCode.VOICE_10_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(YrityspakettiProductCode.VOICE_2_M),
    offerCode: YrityspakettiOfferCode.VOICE_2_M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice1000Mbps),
    offerCode: FiveGVoiceOfferCode.Voice1000Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice600Mbps),
    offerCode: FiveGVoiceOfferCode.Voice600Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice300Mbps),
    offerCode: FiveGVoiceOfferCode.Voice300Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGMobileBroadbandProductCode.MBB_1000_M),
    offerCode: FiveGMobileBroadbandOfferCode.MBB_1000_M,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGMobileBroadbandProductCode.MBB_600_M),
    offerCode: FiveGMobileBroadbandOfferCode.MBB_600_M,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGMobileBroadbandProductCode.MBB_300_M),
    offerCode: FiveGMobileBroadbandOfferCode.MBB_300_M,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGMobileBroadbandProductCode.MBB_100_M),
    offerCode: FiveGMobileBroadbandOfferCode.MBB_100_M,
    modelType: ModelType.MobileBroadband,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice5gPlus1000Mbps),
    offerCode: FiveGVoiceOfferCode.Voice5gPlus1000Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice5gPlus600Mbps),
    offerCode: FiveGVoiceOfferCode.Voice5gPlus600Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice5gPlus300Mbps),
    offerCode: FiveGVoiceOfferCode.Voice5gPlus300Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice50Mbps),
    offerCode: FiveGVoiceOfferCode.Voice50Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(FiveGVoiceProductCode.Voice10Mbps),
    offerCode: FiveGVoiceOfferCode.Voice10Mbps,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(VoiceYrityspuheLiteProductCode.VoiceYrityspuheLite300M),
    offerCode: ElisaYrityspuheUusiOfferCode.VoiceYrityspuheLite300M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(VoiceYrityspuheLiteProductCode.VoiceYrityspuheLite100M),
    offerCode: ElisaYrityspuheUusiOfferCode.VoiceYrityspuheLite100M,
    modelType: ModelType.YrityspuheUusi,
  },
  {
    dataLimitation: populateDataLimitationMessage(VoiceYrityspuheLiteProductCode.VoiceYrityspuheLite50M),
    offerCode: ElisaYrityspuheUusiOfferCode.VoiceYrityspuheLite50M,
    modelType: ModelType.YrityspuheUusi,
  },
];

export const commercialProductContent = (): ProductContent[] => [
  ...mobileBroadbandFiveGContent(),
  ...mobileVoiceContent(),
];

export const PopoverContent = ({ children }: React.PropsWithChildren) => (
  <div className={`${dsClass.TEXT_ALIGN_LEFT} ${dsClass.TEXT_S}`}>{children}</div>
);
